/**
 * PageHeader — the standard opening block for every non-home page, mirrored
 * from the client's reference: full-bleed photo under a black scrim, flame
 * eyebrow, huge condensed uppercase title on the left, intro paragraph bottom
 * right. Clears the fixed nav with var(--nav-h).
 *
 * Two optional slots: `breadcrumb` above the eyebrow, `children` under the intro.
 */
export interface PageHeaderProps {
  eyebrow: string;
  title: string;
  intro?: string;
  /** Desktop image URL or public path, e.g. `HEADER.process.src` from #/lib/images. */
  image: string;
  /**
   * Optional narrow-viewport crop, served below 768px so a phone does not
   * download the desktop file. Same breakpoint the home hero uses.
   */
  imageMobile?: string;
  imageAlt: string;
  /**
   * Rendered above the eyebrow, top left. For breadcrumb-style navigation such
   * as a "back to all services" link, which needs to be found before the page
   * is read rather than after.
   */
  breadcrumb?: React.ReactNode;
  /** Extras rendered under the intro, bottom right (rating box, badges, etc.). */
  children?: React.ReactNode;
}

export function PageHeader({
  eyebrow,
  title,
  intro,
  image,
  imageMobile,
  imageAlt,
  breadcrumb,
  children,
}: PageHeaderProps) {
  return (
    <section className="relative isolate bg-graphite pt-[var(--nav-h)]">
      {/* Photo + scrim */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <picture>
          {imageMobile && (
            <source media="(max-width: 767px)" srcSet={imageMobile} />
          )}
          <img
            src={image}
            alt={imageAlt}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
      </div>

      <div className="mx-auto flex min-h-[46vh] max-w-7xl flex-col justify-end px-4 pb-14 pt-16 sm:px-6 lg:min-h-[52vh] lg:px-8 lg:pb-20 lg:pt-24">
        <div className="lg:grid lg:grid-cols-12 lg:items-end lg:gap-10">
          <div className="lg:col-span-7">
            {breadcrumb && <div className="mb-6">{breadcrumb}</div>}
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame">
              {eyebrow}
            </p>
            <h1 className="font-heading mt-4 font-stretch-[62.5%] text-4xl font-black uppercase leading-[0.9] text-white sm:text-5xl lg:text-7xl">
              {title}
            </h1>
          </div>
          {(intro || children) && (
            <div className="mt-6 lg:col-span-5 lg:mt-0 lg:self-end">
              {intro && (
                <p className="text-base leading-relaxed text-white/80">{intro}</p>
              )}
              {children && <div className={intro ? "mt-8" : undefined}>{children}</div>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
